import { apiFetch } from "./api";
import { fetchAllPages } from "./paging";
import type { Paginated } from "./platform-api";

/**
 * Managed departments (FEAT-2026-003 / ADR-0008). Tenant-scoped lookup
 * list used by employees, tenant users, sites (M-N) and announcement
 * targeting. Referenced departments can only be deactivated (BR-071).
 */
export interface Department {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  isActive: boolean;
  /** Org tree (ADR-0010): roots = companies; path = ancestor-id chain incl. self. */
  parentId: string | null;
  path: string;
  depth: number;
  /** Ancestor names incl. self, root-first — join for "A › B › C" labels. */
  pathNames: string[];
  counts: {
    employees: number;
    users: number;
    sites: number;
    children: number;
    subtreeEmployees: number;
  };
  createdAt: string;
  updatedAt: string;
}

/** "Company › Division › Unit" display label. */
export function departmentLabel(d: Pick<Department, "pathNames" | "name">): string {
  return d.pathNames?.length ? d.pathNames.join(" › ") : d.name;
}

export interface ListDepartmentsQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  /** Default false → active only (pickers). The admin page passes true. */
  includeInactive?: boolean;
}

export interface CreateDepartmentBody {
  name: string;
  description?: string;
  /** Omit = top level (company). */
  parentId?: string;
}

export interface UpdateDepartmentBody {
  name?: string;
  description?: string;
  isActive?: boolean;
  /** Move: omit = no change; null = make it a top-level company. */
  parentId?: string | null;
}

function toQuery(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "" || v === false) continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

export const departmentsApi = {
  list: (q: ListDepartmentsQuery = {}) =>
    apiFetch<Paginated<Department>>(`/departments${toQuery(q as Record<string, unknown>)}`),

  /** Every department, across pages — for pickers and the tree. A tenant can exceed one page (KPTM has 250). */
  listAll: (q: Omit<ListDepartmentsQuery, "page" | "pageSize"> = {}) =>
    fetchAllPages((page, pageSize) => departmentsApi.list({ ...q, page, pageSize })),

  get: (id: string) => apiFetch<Department>(`/departments/${id}`),

  create: (body: CreateDepartmentBody) =>
    apiFetch<Department>(`/departments`, { method: "POST", body }),

  update: (id: string, body: UpdateDepartmentBody) =>
    apiFetch<Department>(`/departments/${id}`, { method: "PATCH", body }),

  /** 409 when referenced — deactivate via update({isActive:false}) instead. */
  remove: (id: string) =>
    apiFetch<{ id: string; deleted: true }>(`/departments/${id}`, { method: "DELETE" }),
};
